/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useMemo, useState } from "react";
import { useGetUsersQuery, UserReadAll } from "src/store/api/generatedApi";
import { useTenantMenuItems } from "src/dashboard/menuItems/tenantMenuItems";
import { MenuItem } from "src/dashboard/menuItems/types";
import { flattenMenuItems } from "src/dashboard/menuItems/utils";
import { useTranslation } from "react-i18next";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { useTenantData } from "contexts/tenant";
import { PermissionsControls } from "./PermissionsControls";
import { PermissionsTable } from "./PermissionsTable";
import { PermissionsTableSkeleton } from "./PermissionsTableSkeleton";
import { PermissionsError } from "./PermissionsError";
import { UserAccessDialog } from "./UserAccessDialog";
import { useTenant } from "hooks/settings";
import { Skeleton } from "@mui/material";

const canUserAccessMenuItem = (
  user: UserReadAll,
  menuItem: MenuItem,
  tenantFeatureFlags: string[]
): boolean => {
  if (!user.is_enabled) return false;
  if (user.is_admin) return true;

  if (
    menuItem.featureFlag &&
    !tenantFeatureFlags.includes(menuItem.featureFlag)
  ) {
    return false;
  }

  if (menuItem.requireAdmin && !user.is_admin) {
    return false;
  }

  const userPermissions = user.permissions?.map((p) => p.permission) ?? [];
  if (menuItem.permissionsToCheck && menuItem.permissionsToCheck.length > 0) {
    const hasRequiredPermission = menuItem.permissionsToCheck.some(
      (permission) => userPermissions.includes(permission)
    );
    if (!hasRequiredPermission) return false;
  }

  return (
    menuItem.requiredPermissions?.every((permission) =>
      userPermissions.includes(permission)
    ) ?? true
  );
};

interface MenuItemStat {
  menuItem: MenuItem;
  userCount: number;
  users: UserReadAll[];
  isChild: boolean;
  parentPath?: string;
}

export const PermissionsOverview: React.FC = () => {
  const { t } = useTranslation();
  const { feature_flags: tenantFeatureFlags } = useTenantData();
  const tenant = useTenant();
  const {
    data: allUsers,
    isFetching: isUsersFetching,
    isError,
    refetch,
  } = useGetUsersQuery({
    tenantName: tenant,
  });
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<{
    menuItem: MenuItem;
    users: UserReadAll[];
  } | null>(null);

  const users = useMemo(
    () =>
      includeAdmins
        ? (allUsers ?? [])
        : (allUsers ?? []).filter((user) => !user.is_admin),
    [allUsers, includeAdmins]
  );

  const tenantMenuItems = useTenantMenuItems();
  const allMenuItems = useMemo(
    () => flattenMenuItems(tenantMenuItems),
    [tenantMenuItems]
  );

  const accessMatrix = useMemo(() => {
    if (!allUsers || isUsersFetching) return {};

    const matrix: Record<string, Record<string, boolean>> = {};

    users.forEach((user) => {
      const userAccess: Record<string, boolean> = {};
      allMenuItems.forEach((menuItem) => {
        userAccess[menuItem.path] = canUserAccessMenuItem(
          user,
          menuItem,
          tenantFeatureFlags
        );
      });
      matrix[user.uid] = userAccess;
    });

    return matrix;
  }, [users, allMenuItems, tenantFeatureFlags, allUsers, isUsersFetching]);

  const menuItemStats: MenuItemStat[] = useMemo(() => {
    if (!allUsers || isUsersFetching) return [];

    const stats: MenuItemStat[] = [];

    tenantMenuItems.forEach((parentItem) => {
      const parentAccessibleUsers = users.filter(
        (user) => accessMatrix[user.uid]?.[parentItem.path]
      );

      stats.push({
        menuItem: parentItem,
        userCount: parentAccessibleUsers.length,
        users: parentAccessibleUsers,
        isChild: false,
      });

      if (parentItem.children) {
        parentItem.children.forEach((childItem) => {
          const childAccessibleUsers = users.filter(
            (user) => accessMatrix[user.uid]?.[childItem.path]
          );

          stats.push({
            menuItem: childItem,
            userCount: childAccessibleUsers.length,
            users: childAccessibleUsers,
            isChild: true,
            parentPath: parentItem.path,
          });
        });
      }
    });

    return stats;
  }, [tenantMenuItems, users, accessMatrix, allUsers, isUsersFetching]);

  const handleMenuItemClick = (menuItem: MenuItem, users: UserReadAll[]) => {
    setSelectedMenuItem({ menuItem, users });
  };

  const handleCloseDialog = () => {
    setSelectedMenuItem(null);
  };

  const handleRetry = () => {
    void refetch();
  };

  return (
    <AccountPageLayout title={t("permissionsOverview")}>
      {isUsersFetching ? (
        <>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <PermissionsTableSkeleton />
        </>
      ) : isError ? (
        <PermissionsError onRetry={handleRetry} />
      ) : (
        <>
          <PermissionsControls
            includeAdmins={includeAdmins}
            onIncludeAdminsChange={setIncludeAdmins}
            userCount={users.length}
            totalUsers={allUsers?.length ?? 0}
          />

          <PermissionsTable
            menuItemStats={menuItemStats}
            onMenuItemClick={handleMenuItemClick}
          />
        </>
      )}

      <UserAccessDialog
        open={selectedMenuItem !== null}
        menuItem={selectedMenuItem?.menuItem ?? null}
        users={selectedMenuItem?.users ?? []}
        onClose={handleCloseDialog}
      />
    </AccountPageLayout>
  );
};
